import { useInfiniteQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import type { FileNode, NodeAncestor } from "../api.ts";
import { formatCount } from "../lib/format.ts";
import { codeOf, messageOf, nodesQuery, useMoveNode } from "../lib/queries.ts";
import dialog from "./ConfirmDialog.module.css";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { FolderBadge } from "./FileBadge.tsx";
import { Icon } from "./Icon.tsx";
import styles from "./MoveDialog.module.css";
import ui from "./ui.module.css";

type Props = { node: FileNode; className?: string; variant?: "frame" | "primary" };

export function MoveButton({ node, className, variant }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`${ui.button} ${className ?? ""}`}
        data-variant={variant}
        onClick={() => setOpen(true)}
      >
        <Icon name="moveTo" />
        Move to…
      </button>
      {open ? <MoveDialog node={node} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function MoveDialog({ node, onClose }: { node: FileNode; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [trail, setTrail] = useState<NodeAncestor[]>([]);
  const [widening, setWidening] = useState<string | null>(null);
  const here = trail.at(-1)?.id ?? null;
  const move = useMoveNode(node.id);
  const listing = useInfiniteQuery(nodesQuery(here));

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const items = listing.data?.pages.flatMap((page) => page.items) ?? [];
  const folders = items.filter((item) => item.kind === "folder" && item.id !== node.id);
  const files = items.filter((item) => item.kind !== "folder").length;
  const dismiss = () => {
    ref.current?.close();
    onClose();
  };
  const close = () => {
    if (!move.isPending) dismiss();
  };
  const submit = (confirmWidening: boolean) =>
    move.mutate(
      {
        id: node.id,
        from: node.parentId,
        parentId: here,
        ...(confirmWidening ? { confirmWidening: true } : {}),
      },
      {
        onSuccess: dismiss,
        onError: (error) => setWidening(codeOf(error) === "move_widens" ? messageOf(error) : null),
      },
    );

  const destination = trail.at(-1)?.name ?? "Files";
  const only = files > 0 ? `, just ${formatCount(files, "file", "files")}` : "";
  const noFolders =
    trail.length === 0
      ? `No folders yet${only}.`
      : files > 0
        ? `No folders inside ${destination}${only}.`
        : `Nothing inside ${destination}.`;
  const failure = move.error && widening === null ? messageOf(move.error) : null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape reaches the dialog as its cancel event
    <dialog
      ref={ref}
      className={dialog.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className={`${dialog.body} ${dialog.form}`}>
        <h2 id={titleId} className={dialog.title}>
          Move {node.name}
        </h2>
        <nav className={styles.trail} aria-label="Folder">
          <button type="button" className={styles.crumb} onClick={() => setTrail([])}>
            <Icon name="files" />
            Files
          </button>
          {trail.map((folder, index) => (
            <Fragment key={folder.id}>
              <Icon name="chevron" className={styles.crumbChevron} />
              <button
                type="button"
                className={styles.crumb}
                aria-current={index === trail.length - 1 ? "true" : undefined}
                onClick={() => setTrail(trail.slice(0, index + 1))}
              >
                {folder.name}
              </button>
            </Fragment>
          ))}
        </nav>

        <div className={styles.list}>
          {listing.isPending ? (
            <p className={styles.note}>Loading…</p>
          ) : folders.length === 0 ? (
            <p className={styles.note}>{noFolders}</p>
          ) : (
            <ul className={styles.folders}>
              {folders.map((folder) => (
                <li key={folder.id}>
                  <button
                    type="button"
                    className={styles.folder}
                    onClick={() => setTrail([...trail, { id: folder.id, name: folder.name }])}
                  >
                    <FolderBadge />
                    <span className={styles.folderText}>
                      <span className={styles.folderName}>{folder.name}</span>
                      <span className={styles.folderCount}>
                        {folder.childCount === 0
                          ? "Empty"
                          : formatCount(folder.childCount, "item", "items")}
                      </span>
                    </span>
                    <Icon name="chevron" className={styles.folderChevron} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {listing.hasNextPage ? (
            <button
              type="button"
              className={`${ui.button} ${styles.more}`}
              onClick={() => void listing.fetchNextPage()}
              disabled={listing.isFetchingNextPage}
            >
              {listing.isFetchingNextPage ? "Loading…" : "Load more folders"}
            </button>
          ) : null}
        </div>

        {failure ? (
          <p className={dialog.error} role="alert">
            {failure}
          </p>
        ) : null}

        <div className={dialog.actions}>
          <button type="button" className={ui.button} onClick={close} disabled={move.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className={ui.button}
            data-variant="primary"
            disabled={move.isPending || here === node.parentId}
            onClick={() => submit(false)}
          >
            {move.isPending ? "Moving…" : here === node.parentId ? "Already here" : "Move here"}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={widening !== null}
        title="This shares it wider"
        confirmLabel={move.isPending ? "Moving…" : "Move anyway"}
        cancelLabel="Keep it where it is"
        pending={move.isPending}
        onCancel={() => {
          setWidening(null);
          move.reset();
        }}
        onConfirm={() => {
          setWidening(null);
          submit(true);
        }}
      >
        <p>{widening}</p>
      </ConfirmDialog>
    </dialog>
  );
}
